import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/Card';
import Input from '@components/ui/Input';
import Button from '@components/ui/Button';
import Badge from '@components/ui/Badge';

import { 
  Search, 
  Plus, 
  Edit2, 
  Trash2, 
  X, 
  FileText,
  Check,
  ChevronLeft,
  Calendar,
  Eye,
  EyeOff
} from 'lucide-react';
import type { Article } from '../../types';

// Mock articles data
const mockArticles: Article[] = [
  {
    id: '1',
    title: 'Neue Ausstellung im REM: Margaret Courtney-Clarke',
    content: 'Im Rahmen der neuen Ausstellung "Geographies of Drought" werden Werke der renommierten Fotografin Margaret Courtney-Clarke gezeigt...',
    excerpt: 'Die neue Ausstellung im REM zeigt eindringliche Bilder von Dürre und Klimawandel.',
    category: 'news',
    status: 'published',
    authorId: '1',
    publishedAt: '2026-01-15T10:00:00Z',
    createdAt: '2026-01-10T14:30:00Z',
  },
  {
    id: '2',
    title: 'Geschichte der Mannheimer Sternwarte',
    content: 'Die Alte Sternwarte Mannheim gehört zu den ältesten Gebäuden der Stadt und ist für Baden-Württemberg einzigartig...',
    excerpt: 'Von 1775 bis 1880 wurde hier astronomische Forschung betrieben.',
    category: 'history',
    status: 'published',
    authorId: '1',
    publishedAt: '2026-01-08T09:00:00Z',
    createdAt: '2026-01-01T11:00:00Z',
  },
  {
    id: '3',
    title: 'Astronomie-Workshop für Kinder',
    content: 'In unserem beliebten Workshop lernen Kinder spielerisch die Grundlagen der Astronomie kennen...',
    excerpt: 'Interaktiver Workshop für junge Sterngucker ab 8 Jahren.',
    category: 'astronomy',
    status: 'draft',
    authorId: '1',
    createdAt: '2026-01-20T16:00:00Z',
  },
  {
    id: '4',
    title: 'Christian Mayer: Hofastronom und Wissenschaftler',
    content: 'Christian Mayer (1719-1783) war der erste Hofastronom der Mannheimer Sternwarte und ein bedeutender Wissenschaftler seiner Zeit...',
    excerpt: 'Ein Blick auf das Leben und Werk des ersten Hofastronomen.',
    category: 'history',
    status: 'published',
    authorId: '1',
    publishedAt: '2025-12-20T10:00:00Z',
    createdAt: '2025-12-15T13:00:00Z',
  },
];

type Category = 'news' | 'astronomy' | 'history';
type Status = 'draft' | 'published';

interface ArticleFormData {
  title: string;
  content: string;
  excerpt: string;
  category: Category;
  status: Status;
}

const emptyFormData: ArticleFormData = {
  title: '',
  content: '',
  excerpt: '',
  category: 'news',
  status: 'draft',
};

function ArticleForm({ 
  article, 
  onSave, 
  onCancel 
}: { 
  article?: Article; 
  onSave: (data: ArticleFormData) => void;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState<ArticleFormData>(
    article ? {
      title: article.title,
      content: article.content,
      excerpt: article.excerpt || '',
      category: article.category,
      status: article.status,
    } : emptyFormData
  );

  const wordCount = formData.content.trim().split(/\s+/).filter(Boolean).length;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const insertFormat = (before: string, after: string = '') => {
    const textarea = document.getElementById('content') as HTMLTextAreaElement;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.content;
    const selected = text.substring(start, end);
    
    const newText = text.substring(0, start) + before + selected + after + text.substring(end);
    setFormData({ ...formData, content: newText });
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, end + before.length);
    }, 0);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <Button 
          type="button" 
          variant="ghost" 
          onClick={onCancel}
          className="gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          Zurück
        </Button>
        <h2 className="text-xl font-bold text-white">
          {article ? 'Artikel bearbeiten' : 'Neuer Artikel'}
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          <Input
            label="Titel *"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Geben Sie einen aussagekräftigen Titel ein"
            required
          />

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Inhalt * {wordCount > 0 && <span className="text-slate-500">({wordCount} Wörter)</span>}
            </label>
            
            {/* Simple formatting toolbar */}
            <div className="flex flex-wrap gap-1 p-2 bg-slate-900 border border-slate-800 border-b-0 rounded-t-lg">
              <button
                type="button"
                onClick={() => insertFormat('**', '**')}
                className="px-3 py-1 text-sm font-bold text-slate-300 hover:text-white hover:bg-slate-800 rounded"
                title="Fett"
              >
                B
              </button>
              <button
                type="button"
                onClick={() => insertFormat('*', '*')}
                className="px-3 py-1 text-sm italic text-slate-300 hover:text-white hover:bg-slate-800 rounded"
                title="Kursiv"
              >
                I
              </button>
              <button
                type="button"
                onClick={() => insertFormat('# ')}
                className="px-3 py-1 text-sm text-slate-300 hover:text-white hover:bg-slate-800 rounded"
                title="Überschrift"
              >
                H1
              </button>
              <button
                type="button"
                onClick={() => insertFormat('## ')}
                className="px-3 py-1 text-sm text-slate-300 hover:text-white hover:bg-slate-800 rounded"
                title="Überschrift 2"
              >
                H2
              </button>
              <button
                type="button"
                onClick={() => insertFormat('- ')}
                className="px-3 py-1 text-sm text-slate-300 hover:text-white hover:bg-slate-800 rounded"
                title="Liste"
              >
                • List
              </button>
              <button
                type="button"
                onClick={() => insertFormat('[', '](url)')}
                className="px-3 py-1 text-sm text-slate-300 hover:text-white hover:bg-slate-800 rounded"
                title="Link"
              >
                Link
              </button>
            </div>

            <textarea
              id="content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Schreiben Sie hier den Inhalt Ihres Artikels..."
              rows={15}
              required
              className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-b-lg rounded-t-none text-white placeholder-slate-500
                focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
                transition-all duration-200 resize-vertical font-mono text-sm leading-relaxed"
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Publish settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Veröffentlichung</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as Status })}
                  className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-lg text-white
                    focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
                    transition-all duration-200"
                >
                  <option value="draft">Entwurf</option>
                  <option value="published">Veröffentlicht</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Kategorie
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as Category })}
                  className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-lg text-white
                    focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
                    transition-all duration-200"
                >
                  <option value="news">Aktuelles</option>
                  <option value="astronomy">Astronomie</option>
                  <option value="history">Geschichte</option>
                </select>
              </div>

              <Button type="submit" className="w-full">
                {formData.status === 'published' ? (
                  <>
                    <Eye className="w-4 h-4 mr-2" />
                    {article ? 'Aktualisieren' : 'Veröffentlichen'}
                  </>
                ) : (
                  <>
                    <EyeOff className="w-4 h-4 mr-2" />
                    Als Entwurf speichern
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Excerpt */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Zusammenfassung</CardTitle>
            </CardHeader>
            <CardContent>
              <textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder="Kurze Zusammenfassung für Übersichten..."
                rows={4}
                maxLength={200}
                className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500
                  focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
                  transition-all duration-200 resize-vertical text-sm"
              />
              <p className="text-right text-xs text-slate-500 mt-2">
                {formData.excerpt.length}/200 Zeichen
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </form>
  );
}

export default function ArticlesManage() {
  const [articles, setArticles] = useState<Article[]>(mockArticles);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<Category | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<Status | 'all'>('all');
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const filteredArticles = useMemo(() => {
    return articles.filter(article => {
      const matchesSearch = 
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.content.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = categoryFilter === 'all' || article.category === categoryFilter;
      const matchesStatus = statusFilter === 'all' || article.status === statusFilter;
      
      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [articles, searchQuery, categoryFilter, statusFilter]);

  const stats = useMemo(() => {
    return {
      total: articles.length,
      published: articles.filter(a => a.status === 'published').length,
      drafts: articles.filter(a => a.status === 'draft').length,
    };
  }, [articles]);

  const handleSave = (formData: ArticleFormData) => {
    if (editingArticle) {
      setArticles(articles.map(a => 
        a.id === editingArticle.id 
          ? { 
              ...a, 
              ...formData,
              publishedAt: formData.status === 'published' && !a.publishedAt 
                ? new Date().toISOString() 
                : a.publishedAt,
            }
          : a
      ));
      setEditingArticle(null);
    } else {
      const newArticle: Article = {
        id: Date.now().toString(),
        ...formData,
        createdAt: new Date().toISOString(),
        publishedAt: formData.status === 'published' ? new Date().toISOString() : undefined,
      };
      setArticles([newArticle, ...articles]);
      setIsCreating(false);
    }
  };

  const handleDelete = (id: string) => {
    setArticles(articles.filter(a => a.id !== id));
    setDeleteConfirm(null);
  };



  const getCategoryBadge = (category: Category) => {
    switch (category) {
      case 'news':
        return <Badge variant="info">Aktuelles</Badge>;
      case 'astronomy':
        return <Badge variant="success">Astronomie</Badge>;
      case 'history':
        return <Badge variant="warning">Geschichte</Badge>;
    }
  };

  const getStatusBadge = (status: Status) => {
    switch (status) {
      case 'published':
        return <Badge variant="success">Veröffentlicht</Badge>;
      case 'draft':
        return <Badge variant="default">Entwurf</Badge>;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (isCreating) {
    return (
      <Card>
        <CardContent className="p-6">
          <ArticleForm onSave={handleSave} onCancel={() => setIsCreating(false)} />
        </CardContent>
      </Card>
    );
  }

  if (editingArticle) {
    return (
      <Card>
        <CardContent className="p-6">
          <ArticleForm 
            article={editingArticle} 
            onSave={handleSave} 
            onCancel={() => setEditingArticle(null)} 
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Artikel</h1>
          <p className="text-slate-400 mt-1">
            Verwalten Sie Nachrichten und Inhalte der Website
          </p>
        </div>
        <Button onClick={() => setIsCreating(true)} size="lg">
          <Plus className="w-5 h-5 mr-2" />
          Neuer Artikel
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-slate-400 text-sm">Gesamt</p>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-slate-400 text-sm">Veröffentlicht</p>
            <p className="text-2xl font-bold text-green-400">{stats.published}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-slate-400 text-sm">Entwürfe</p>
            <p className="text-2xl font-bold text-slate-400">{stats.drafts}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <Input
            placeholder="Artikel suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as Category | 'all')}
          className="px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-lg text-white
            focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
            transition-all duration-200"
        >
          <option value="all">Alle Kategorien</option>
          <option value="news">Aktuelles</option>
          <option value="astronomy">Astronomie</option>
          <option value="history">Geschichte</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as Status | 'all')}
          className="px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-lg text-white
            focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
            transition-all duration-200"
        >
          <option value="all">Alle Status</option>
          <option value="published">Veröffentlicht</option>
          <option value="draft">Entwurf</option>
        </select>
      </div>

      {/* Articles List */}
      <Card>
        <CardContent className="p-0">
          {filteredArticles.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">
                Keine Artikel gefunden
              </h3>
              <p className="text-slate-400">
                {searchQuery || categoryFilter !== 'all' || statusFilter !== 'all'
                  ? 'Passen Sie Ihre Filter an.'
                  : 'Erstellen Sie Ihren ersten Artikel.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {filteredArticles.map((article) => (
                <div 
                  key={article.id} 
                  className="px-6 py-4 hover:bg-slate-900/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-white font-medium truncate">{article.title}</h3>
                        {getStatusBadge(article.status)}
                      </div>
                      <p className="text-slate-400 text-sm mb-2 line-clamp-2">
                        {article.excerpt || article.content.substring(0, 150) + '...'}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        {getCategoryBadge(article.category)}
                        <span className="flex items-center gap-1 text-slate-500">
                          <Calendar className="w-4 h-4" />
                          {article.status === 'published' 
                            ? `Veröffentlicht: ${formatDate(article.publishedAt)}`
                            : `Erstellt: ${formatDate(article.createdAt)}`
                          }
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingArticle(article)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      {deleteConfirm === article.id ? (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm(null)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(article.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm(article.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
